// ductCalc.js — motor de cálculo puro para la Calculadora de Conductos.
// Sin React ni Firebase: dado un proyecto (estancias + tramos + config),
// calcula la sección de cada tramo (método de velocidad constante), la
// pieza de plancha desplegada de cada uno (fabricación de una sola pieza
// doblada: ancho = perímetro de la sección, largo = longitud del tramo) y
// el empaquetado de todas las piezas sobre planchas de 3x1,2m.
// Todo en milímetros salvo donde se indique explícitamente m²/m³.
//
// También conoce la geometría de trazado (trazadoPx) de cada tramo y la
// posición del equipo — no para dibujar nada, sino para detectar por
// proximidad qué tramo alimenta a cuál (mismo criterio que ya usa
// PlanoTracer.js para la embocadura equipo→tramo) y así propagar el caudal
// real por la red: un tronco lleva la suma de lo que cuelga de él, sin que
// haga falta marcar las mismas estancias a mano en cada nivel.

import { distanciaPx, puntoMasCercanoEnPolilinea } from './planoGeo';

export const DEFAULTS = {
  // Velocidad objetivo SOLO del primer tramo de cada rama que arranca del
  // equipo (cap. 11.2 del manual: "se hará coincidir el caudal de impulsión
  // con la velocidad máxima deseada del aire"). El resto de la red ya no se
  // fija por velocidad, sino por recuperación estática (ver más abajo) —
  // por eso ya no existe un "velocidadDerivacion" independiente.
  velocidadTroncal: 5,       // m/s
  caudalPorM2: 35,           // m3/h por m2 de estancia
  merma: 0.12,               // 12%
  panelAnchoMm: 3000,        // largo de la plancha
  panelAltoMm: 1200,         // ancho de la plancha
  areaTeAproxM2: 0.45,       // área aproximada de plancha por pieza de unión T/Y — solo como red de seguridad cuando no se puede calcular la forma real (ver piezasUnion)
  margenEmpalmeMm: 50,       // se añade al largo de cada pieza para la unión
  redondeoSeccionMm: 50,     // redondeo práctico del ancho de sección
  // Margen real sobre el perímetro puro para la pieza de una sola plancha
  // plegada: regla "-2 +4 +4 +4" (cap. 3.1 del manual del instalador,
  // "figura conducto recto") sobre los 4 lados, más 9cm de solape para el
  // cierre longitudinal = perímetro + 190mm — debe coincidir exactamente con
  // el ancho de tira que calcula fabricacionTramoRecto (x5 más abajo), o el
  // dibujo real se saldría del hueco que le reserva el encaje en la plancha.
  margenPerimetroMm: 190,
  // Coeficientes del método de recuperación estática (cap. 11.3 del manual)
  // — copiados literalmente de la hoja de cálculo oficial "Dimensionado de
  // redes de conductos URSA AIR" (www.ursa.es), no inventados: rugosidad del
  // panel URSA AIR Alu-Alu (hoja ACCESORIOS, tabla de tipos de conducto —
  // hay otros paneles con 0,005), el margen del 50% que la propia plantilla
  // aplica sobre la pérdida lineal de cada tramo (cubre lo que no se
  // calcula explícitamente por curvas y derivaciones), y el coeficiente de
  // recuperación estática 0,75 en cada empalme.
  rugosidadDucto: 0.004,
  factorPerdidaLocal: 1.5,
  factorRecuperacion: 0.75,
};

// Tolerancia (en las mismas unidades "world" que trazadoPx/equipoPos, no
// mm reales) para considerar que el arranque de un tramo está conectado a
// otro tramo o al equipo — igual de holgada que el umbral de embocadura en
// PlanoTracer.js (EQUIPO_W+30), no configurable desde la UI.
const TOL_CONEXION_PX = 40;

export function calcularCaudalEstancia(estancia, caudalPorM2) {
  const manual = Number(estancia.caudalManual);
  if (Number.isFinite(manual) && manual > 0) return manual;
  const area = Number(estancia.area) || 0;
  return area * caudalPorM2;
}

export function calcularCaudalTramo(tramo, caudalPorEstanciaId) {
  return (tramo.estanciasIds || []).reduce((sum, id) => sum + (caudalPorEstanciaId[id] || 0), 0);
}

export function calcularSeccion({ caudalM3h, velocidad, alturaMm, redondeoMm }) {
  const seccionM2 = velocidad > 0 ? caudalM3h / (3600 * velocidad) : 0;
  const altoMm = alturaMm;
  const anchoCrudoMm = altoMm > 0 ? (seccionM2 * 1e6) / altoMm : 0;
  const anchoMm = Math.max(redondeoMm, Math.ceil(anchoCrudoMm / redondeoMm) * redondeoMm);
  return { anchoMm, altoMm, seccionM2: (anchoMm * altoMm) / 1e6 };
}

// ── Dimensionado por recuperación estática (cap. 11.3 del manual) ──────────
// Mismas fórmulas, literales, que la hoja de cálculo oficial "Dimensionado
// de redes de conductos" de URSA AIR (hoja CALCULO): diámetro equivalente
// circular (Huebscher), velocidad y pérdida de carga sobre ese diámetro
// equivalente (no sobre el área real rectangular — así es como lo define el
// propio método), y recuperación estática entre tramo y tramo. Se llegó a
// esta fórmula exacta leyendo las fórmulas de las celdas de la plantilla
// descargada de www.ursa.es, no por deducción propia.

// Diámetro equivalente circular (mm) de una sección rectangular — hoja
// CALCULO, columna F "D.equi(A*H)": 1,3·(a·b)^0,625 / (a+b)^0,25, con a y b
// en metros.
export function diametroEquivalenteMm(anchoMm, altoMm) {
  if (anchoMm <= 0 || altoMm <= 0) return 0;
  const a = anchoMm / 1000, b = altoMm / 1000;
  return 1000 * 1.3 * Math.pow(a * b, 0.625) / Math.pow(a + b, 0.25);
}

// Velocidad (m/s) sobre el área circular del diámetro equivalente — columna
// G "Velocidad (m/s)".
export function velocidadEquivalenteMs(caudalM3h, diametroEquivMm) {
  if (diametroEquivMm <= 0) return 0;
  const dM = diametroEquivMm / 1000;
  return caudalM3h / (3600 * Math.PI * dM * dM / 4);
}

// Pérdida de carga por metro lineal (mm.c.a./m) — columna I "P.Presion".
// `rugosidad` es el coeficiente del tipo de panel URSA AIR (hoja ACCESORIOS
// de la plantilla).
export function perdidaPorMetro(velocidadMs, diametroEquivMm, rugosidad) {
  if (diametroEquivMm <= 0) return 0;
  const dM = diametroEquivMm / 1000;
  return 0.102 * 2 * 1.2 * rugosidad * velocidadMs * velocidadMs / dM;
}

// Ancho (mm) que, a una altura fija, hace que la velocidad equivalente
// coincida con una velocidad objetivo — para el primer tramo de cada rama
// que arranca del equipo (cap. 11.2: "se hará coincidir el caudal de
// impulsión con la velocidad máxima deseada"). El diámetro equivalente (y
// por tanto la velocidad) varía de forma monótona con el ancho a altura
// fija, así que basta una búsqueda binaria — la propia plantilla resuelve
// esto mismo a mano, por tanteo, en la celda del ancho.
export function anchoParaVelocidadObjetivo(caudalM3h, altoMm, velocidadObjetivoMs) {
  if (caudalM3h <= 0 || altoMm <= 0 || velocidadObjetivoMs <= 0) return 0;
  let lo = 1, hi = 20000;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    const v = velocidadEquivalenteMs(caudalM3h, diametroEquivalenteMm(mid, altoMm));
    if (v > velocidadObjetivoMs) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

// Ancho (mm) que, a una altura fija, hace que la pérdida de carga de este
// tramo (pérdida por metro × longitud × factorPerdidaLocal, el margen que
// la propia plantilla aplica sobre la pérdida lineal para cubrir lo que no
// calcula explícitamente por curvas/derivaciones) iguale la recuperación
// estática producida por la caída de velocidad respecto al tramo anterior
// — cap. 11.3, método de recuperación estática. A más ancho, menos
// velocidad: la pérdida baja y la recuperación sube, así que el residual
// (pérdida − recuperación) es monótono decreciente y hay un único punto de
// equilibrio — igual que la propia plantilla resuelve "hasta que dé Ok"
// probando anchos a mano, aquí por búsqueda binaria.
export function anchoParaRecuperacionEstatica(caudalM3h, altoMm, longitudM, velocidadAnteriorMs, rugosidad, factorPerdidaLocal, factorRecuperacion) {
  if (caudalM3h <= 0 || altoMm <= 0) return 0;
  const residual = (anchoMm) => {
    const de = diametroEquivalenteMm(anchoMm, altoMm);
    const v = velocidadEquivalenteMs(caudalM3h, de);
    const perdida = perdidaPorMetro(v, de, rugosidad) * longitudM * factorPerdidaLocal;
    const recuperacion = factorRecuperacion * (velocidadAnteriorMs * velocidadAnteriorMs - v * v) / 16;
    return perdida - recuperacion;
  };
  let lo = 1, hi = 20000;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (residual(mid) > 0) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

// ── Fichas de fabricación (método URSA AIR "tapas y paredes") ──────────────
// Reproducen, en mm y en el sistema de coordenadas del propio panel (x = a
// lo largo del panel, hacia "continuación de panel"; y = a lo ancho, y=0 en
// el borde de la hembra / "mecanizado" inferior), la secuencia de líneas
// que describe el manual del instalador para cada figura — no solo el
// contorno final, para que un técnico que no lo ha hecho nunca pueda
// seguir el paso a paso tal cual lo haría con el manual en la mano.

const mmACm = (mm) => Math.round(mm) / 10;
const linea = (x1, y1, x2, y2, extra = {}) => ({ desde: { x: x1, y: y1 }, hasta: { x: x2, y: y2 }, ...extra });
const longitudSegmento = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
const ladosPoligono = (puntos) => puntos.map((p, i) => Math.round(longitudSegmento(p, puntos[(i + 1) % puntos.length])));

// Recorte de un polígono convexo por un semiplano (recta = punto + dirección,
// se conserva el lado donde cae "puntoAConservar") — algoritmo de
// Sutherland-Hodgman de un solo borde. Se usa para cortar la esquina lejana
// del cuadro de construcción del codo con la línea de 45º desplazada, sin
// tener que adivinar a mano qué par de bordes atraviesa esa línea (depende
// de las proporciones ANCHO/20cm y no siempre son los mismos dos bordes).
function ladoDeRecta(punto, p, d) {
  return d.x * (punto.y - p.y) - d.y * (punto.x - p.x);
}
function interseccionSegmentoConRecta(a, b, p, d) {
  const ex = b.x - a.x, ey = b.y - a.y;
  const denom = d.x * ey - d.y * ex;
  if (Math.abs(denom) < 1e-9) return null;
  const t = (d.x * (p.y - a.y) - d.y * (p.x - a.x)) / denom;
  return { x: a.x + t * ex, y: a.y + t * ey };
}
function recortarPoligonoPorRecta(poligono, p, d, puntoAConservar) {
  const signoRef = Math.sign(ladoDeRecta(puntoAConservar, p, d)) || 1;
  const resultado = [];
  for (let i = 0; i < poligono.length; i++) {
    const actual = poligono[i], siguiente = poligono[(i + 1) % poligono.length];
    const sActual = Math.sign(ladoDeRecta(actual, p, d));
    const sSiguiente = Math.sign(ladoDeRecta(siguiente, p, d));
    if (sActual === signoRef || sActual === 0) resultado.push(actual);
    if (sActual !== 0 && sSiguiente !== 0 && sActual !== sSiguiente) {
      const corte = interseccionSegmentoConRecta(actual, siguiente, p, d);
      if (corte) resultado.push(corte);
    }
  }
  return resultado;
}

// Pared o tabica (cap. 4.1 del manual): el ancho de plancha que hace falta
// para cortar una tira de pared es siempre 9cm + (ALTO_conducto-5cm) + 9cm,
// pase lo que pase de qué figura la vaya a usar — los 9cm de cada extremo
// son donde pasa la herramienta azul para el cierre longitudinal. El largo
// es la suma de los lados que le correspondan (ya con las correcciones de
// esquina que pida cada figura aplicadas antes de llamar aquí). El dibujo
// esquemático de la tira (dibujarTira en el PDF) NO está a escala real de
// longitud — solo representa la sección transversal 9+ALTO-5+9 con sus 2
// cortes azules — así que las instrucciones de esquina (que son a lo largo,
// no del ancho) van aparte, como texto en `instrucciones`, no como marcas
// de posición en la tira.
function piezaPared(nombre, ladosMm, altoConductoMm, instrucciones = []) {
  const anchoPlancha = Math.round(90 + (altoConductoMm - 50) + 90);
  const largoTotal = Math.round(ladosMm.reduce((a, b) => a + b, 0));
  const marcas = [
    { x: 90, herramienta: 'Azul', etiqueta: '9cm — cierre longitudinal' },
    { x: anchoPlancha - 90, herramienta: 'Azul', etiqueta: '9cm — cierre longitudinal' },
  ];
  return {
    nombre,
    lados: ladosMm.map(Math.round),
    anchoPiezaMm: anchoPlancha,
    largoPiezaMm: largoTotal,
    tira: { anchoMm: anchoPlancha, largoMm: largoTotal, marcas },
    instrucciones,
  };
}

// Pared a partir de un tramo del contorno YA calculado de una tapa (entre
// dos vértices, normalmente los extremos de dos bocas consecutivas),
// aplicando automáticamente en cada vértice intermedio la regla del cap.
// 4.1 (esquinas entrantes/salientes) — en vez de que cada figura calcule a
// mano cuántas esquinas tiene su pared y qué restar (así se coló antes el
// -10mm fijo del codo, que no correspondía a la regla real de 0,5/1/0,5cm),
// se camina el tramo real del contorno y se decide el tipo de esquina con
// el signo del giro (producto cruzado) en cada vértice — igual criterio que
// ya usa ladoDeRecta para saber a qué lado de una recta cae un punto.
function piezaParedDesdeContorno(nombre, contorno, iInicio, iFin, altoConductoMm) {
  const segmentos = [];
  const instrucciones = [];
  let ladoNum = 1;
  let anterior = contorno[iInicio];
  for (let i = iInicio + 1; i <= iFin; i++) {
    const actual = contorno[i];
    let largo = longitudSegmento(anterior, actual);
    if (i < iFin) {
      const siguiente = contorno[i + 1];
      const cruz = (actual.x - anterior.x) * (siguiente.y - actual.y) - (actual.y - anterior.y) * (siguiente.x - actual.x);
      if (cruz > 0) {
        // esquina entrante: se resta 0,5cm a cada lado que forma la esquina
        largo -= 5;
        instrucciones.push(`entre el lado ${ladoNum} y el ${ladoNum + 1}: esquina entrante — corta con el cuchillo hasta el revestimiento exterior sin cortarlo, y resta 0,5cm a cada lado (ya aplicado en las medidas)`);
      } else {
        instrucciones.push(`entre el lado ${ladoNum} y el ${ladoNum + 1}: esquina saliente — deja una ranura de 1cm de ancho al montarla`);
      }
      ladoNum++;
    }
    segmentos.push(Math.max(0, largo));
    anterior = actual;
  }
  return piezaPared(nombre, segmentos, altoConductoMm, instrucciones);
}

// Tramo recto (cap. 3.1, "método pieza") — regla "-2 +4 +4 +4" sobre los 4
// lados de la sección en el orden ancho→alto→ancho→alto: el maneral rojo
// (ingletes en V) pasa por las 3 primeras líneas, y el azul (cierre
// longitudinal) por la 4ª — luego 9cm más de solape antes del corte final
// con cuchillo (el propio manual da el total como "A+B+A+B+19cm", 9cm más
// de lo que suman las 4 líneas, no 5 como se tenía antes).
export function fabricacionTramoRecto(anchoSeccionMm, altoSeccionMm, largoPiezaMm) {
  const A = anchoSeccionMm, B = altoSeccionMm;
  const x1 = A - 20;
  const x2 = x1 + (B + 40);
  const x3 = x2 + (A + 40);
  const x4 = x3 + (B + 40);
  const x5 = x4 + 90;
  const marcas = [
    { x: x1, herramienta: 'Rojo', etiqueta: `ANCHO -2cm = ${mmACm(x1)}cm` },
    { x: x2, herramienta: 'Rojo', etiqueta: `+ ALTO +4cm = ${mmACm(x2 - x1)}cm` },
    { x: x3, herramienta: 'Rojo', etiqueta: `+ ANCHO +4cm = ${mmACm(x3 - x2)}cm` },
    { x: x4, herramienta: 'Azul', etiqueta: `+ ALTO +4cm = ${mmACm(x4 - x3)}cm — cierre longitudinal` },
    { x: x5, herramienta: 'Cuchillo', etiqueta: `+ 9cm solape (separa la pieza del resto del panel)` },
  ];
  return {
    tipo: 'tramoRecto',
    titulo: `Tramo recto — ${Math.round(A)}×${Math.round(B)} mm`,
    tira: { anchoMm: x5, largoMm: largoPiezaMm, marcas },
    notas: ['Pasa la herramienta azul en ambos extremos de la pieza ya recortada para el machihembrado de unión con el siguiente tramo.'],
    paredes: [],
  };
}

// Tramo recto de grandes dimensiones (cap. 3.2, "construcción de un conducto
// recto con dos piezas (L)") — el manual recomienda este método a partir de
// secciones grandes (≈70x70cm), precisamente porque la tira de una sola
// pieza (fabricacionTramoRecto) deja de caber en el ancho de la plancha. Son
// 2 piezas en L idénticas, cada una con un solo pliegue (rojo en ANCHO-2cm)
// y su propio cierre (azul en +ALTO+4cm) — a diferencia de la pieza única,
// que necesita 3 pliegues + 1 cierre porque da la vuelta completa a la
// sección. Las 2 piezas se montan entre sí grapando y encintando, igual que
// el cierre longitudinal de la pieza única, una vez por cada uno de los 2
// lados abiertos de la L.
export function fabricacionTramoRectoDosPiezas(anchoSeccionMm, altoSeccionMm, largoPiezaMm) {
  const A = anchoSeccionMm, B = altoSeccionMm;
  const x1 = A - 20;
  const x2 = x1 + (B + 40);
  const marcas = [
    { x: x1, herramienta: 'Rojo', etiqueta: `ANCHO -2cm = ${mmACm(x1)}cm` },
    { x: x2, herramienta: 'Azul', etiqueta: `+ ALTO +4cm = ${mmACm(x2 - x1)}cm — cierre de unión con la otra pieza` },
  ];
  return {
    tipo: 'tramoRectoDosPiezas',
    titulo: `Tramo recto, 2 piezas en L — ${Math.round(A)}×${Math.round(B)} mm`,
    tira: { anchoMm: x2, largoMm: largoPiezaMm, marcas },
    notas: [
      'Sección grande: no cabe en una sola tira de plancha, así que se hacen 2 piezas en L iguales a partir de este patrón (repite el mismo trazado en la segunda pieza).',
      'Se montan grapando y encintando cada uno de los 2 lados abiertos de las L — igual que el cierre longitudinal del tramo recto de una sola pieza.',
    ],
    paredes: [],
  };
}

// Codo de 90º (cap. 5.1): rectángulo (ANCHO+2)×(20+ANCHO+2+20), centro en
// la intersección de las 2 primeras líneas, marcas a 10cm, diagonal a 45º
// entre ellas, y línea de corte paralela a la diagonal a ANCHO+2cm.
export function fabricacionCodo90(anchoSeccionMm, altoConductoMm) {
  const d = anchoSeccionMm + 20;
  const S = 200 + d;
  const centro = { x: d, y: 200 };
  const p1 = { x: d + 100, y: 200 };
  const p2 = { x: d, y: 100 };
  // Desplazamiento PERPENDICULAR a la diagonal (no a lo largo de ella): la
  // diagonal tiene dirección (1,1), su perpendicular es (-1,1)/√2. Se
  // desplaza hacia el lado que se aleja del origen (hembra), para recortar
  // solo la esquina lejana del cuadro de construcción, no la cercana.
  const off = d / Math.SQRT2;
  const p1o = { x: p1.x - off, y: p1.y + off };
  const rectangulo = [{ x: 0, y: 0 }, { x: S, y: 0 }, { x: S, y: S }, { x: 0, y: S }];
  const contorno = recortarPoligonoPorRecta(rectangulo, p1o, { x: 1, y: 1 }, { x: 0, y: 0 });
  // Los vértices que no son ninguna de las 4 esquinas originales son los que
  // ha insertado el recorte — son los extremos de la línea de corte final.
  const esEsquinaOriginal = (v) => rectangulo.some(c => Math.hypot(c.x - v.x, c.y - v.y) < 1e-3);
  const nuevosVertices = contorno.filter(v => !esEsquinaOriginal(v));

  const lineas = [
    linea(d, 0, d, S, { herramienta: 'Referencia', etiqueta: `1) ANCHO+2 = ${mmACm(d)}cm, desde "final de panel"` }),
    linea(0, 200, S, 200, { herramienta: 'Referencia', etiqueta: `2) 20cm, desde mecanizado hembra` }),
    linea(0, S, S, S, { herramienta: 'Referencia', etiqueta: `3) ANCHO+2 más, paralela a la línea 2` }),
    linea(S, 0, S, S, { herramienta: 'Referencia', etiqueta: `4) 20cm más, paralela a la línea 1` }),
    linea(centro.x, centro.y, p1.x, p1.y, { herramienta: 'Referencia', etiqueta: '5) marca a 10cm a la derecha del centro' }),
    linea(centro.x, centro.y, p2.x, p2.y, { herramienta: 'Referencia', etiqueta: '6) marca a 10cm hacia abajo del centro' }),
    linea(p2.x, p2.y, p1.x, p1.y, { herramienta: 'Referencia', etiqueta: '7) diagonal a 45º entre las dos marcas' }),
  ];
  if (nuevosVertices.length === 2) lineas.push(linea(nuevosVertices[0].x, nuevosVertices[0].y, nuevosVertices[1].x, nuevosVertices[1].y, { herramienta: 'Cuchillo', etiqueta: `8) línea de corte final, paralela a la diagonal a ${mmACm(d)}cm` }));

  const alto = altoConductoMm || anchoSeccionMm;

  // Paredes reales: la tapa (contorno = [v0=origen, v1=(S,0), v2=(S,S),
  // corte1, corte2] cuando hay chaflán — v3=(0,S) es la esquina que se
  // achafla) tiene 2 bocas (entrada: corte2→v0; salida: corte1→corte2) y 2
  // caminos de pared entre ellas: v1→v2→corte1 (con un giro real en v2) y
  // corte2→v0 (directo, sin giro). Se aplica la regla de esquinas del cap.
  // 4.1 (piezaParedDesdeContorno) al primero, que sí tiene un vértice
  // intermedio — el segundo no necesita corrección al no tener ninguno.
  const paredes = [];
  if (nuevosVertices.length === 2) {
    const [corte1, corte2] = nuevosVertices;
    paredes.push(piezaParedDesdeContorno('Pared 1', [{ x: 0, y: 0 }, { x: S, y: 0 }, { x: S, y: S }, corte1], 1, 3, alto));
    paredes.push(piezaPared('Pared 2', [longitudSegmento(corte2, { x: 0, y: 0 })], alto));
  } else {
    // Caso degenerado sin chaflán (ANCHO tan grande que la diagonal no
    // corta ningún borde): red de seguridad con el lado recto de siempre.
    paredes.push(piezaPared('Pared', [S], alto));
  }

  return {
    tipo: 'codo90',
    titulo: `Codo 90º — ${Math.round(anchoSeccionMm)}mm`,
    tapa: { anchoMm: S, altoMm: S, contornoMm: contorno, lineas, unidades: 2 },
    notas: [
      'Boca de entrada: hembra (marcada desde "final de panel"). Boca de salida: haz el macho con la herramienta negra tras cortar.',
      'Se necesitan 2 tapas iguales (superior e inferior).',
    ],
    paredes,
  };
}

// Derivación / pantalón (Y): entrada A, rama derivada B, salida recta C.
// El manual (cap. 6.1) describe esta tapa con un método de marcado (líneas,
// centro, diagonal a 45º) que no se pudo verificar al 100% en su momento
// (solo texto OCR del PDF, sin la imagen). El usuario ha aportado después
// una foto de una instalación real: la rama se funde con el tronco en un
// chaflán a 45º, no en una T recta de 90º — así que las 2 esquinas cóncavas
// donde la rama se une a la columna se achaflanan.
//
// A diferencia del codo (rectángulo convexo, donde recortarPoligonoPorRecta
// funciona bien porque una recta solo puede cruzar un polígono convexo en 2
// puntos), este contorno es un octógono CÓNCAVO por la propia rama — una
// recta de corte a 45º que pase cerca de una esquina puede cruzar también
// otra arista lejana del contorno (probado: para 700/200/700mm la línea de
// corte de la esquina rama/salida terminaba cruzando también el lado
// derecho del tronco, recortando media pieza en vez de solo la esquina). Por
// eso aquí NO se usa el recorte por semiplano infinito, sino un empalme
// local: se sustituye el vértice cóncavo por sus dos puntos de marca
// (a `chamfer` mm de distancia por cada arista adyacente), sin tocar el
// resto del contorno — válido siempre que `chamfer` sea menor que esas dos
// aristas, que es justo lo que fuerza el cálculo de `chamfer` más abajo.
function unidad(a, b) {
  const l = longitudSegmento(a, b) || 1;
  return { x: (b.x - a.x) / l, y: (b.y - a.y) / l };
}
function achaflanarVertice(contorno, indice, chamfer) {
  const n = contorno.length;
  const v = contorno[indice];
  const anterior = contorno[(indice - 1 + n) % n];
  const siguiente = contorno[(indice + 1) % n];
  const m1 = { x: v.x + unidad(v, anterior).x * chamfer, y: v.y + unidad(v, anterior).y * chamfer };
  const m2 = { x: v.x + unidad(v, siguiente).x * chamfer, y: v.y + unidad(v, siguiente).y * chamfer };
  const contornoNuevo = [...contorno.slice(0, indice), m1, m2, ...contorno.slice(indice + 1)];
  return { contorno: contornoNuevo, m1, m2 };
}
export function fabricacionDerivacion(anchoAMm, anchoBMm, anchoCMm, altoConductoMm) {
  const A = anchoAMm, B = anchoBMm, C = anchoCMm;
  const dA = A + 20, dB = B + 20, dC = C + 20;
  const Lspine = Math.round(Math.max(dA, dC, dB) * 2);
  const brLen = 150; // longitud del muñón de la rama — suficiente para machihembrar con el tramo de la derivación
  const xBranchStart = Lspine / 2 - dB / 2;
  const xBranchEnd = Lspine / 2 + dB / 2;

  const v0 = { x: 0, y: 0 }, v1 = { x: Lspine, y: 0 }, v2 = { x: Lspine, y: dC };
  const v3 = { x: xBranchEnd, y: dC }, v4 = { x: xBranchEnd, y: dC + brLen };
  const v5 = { x: xBranchStart, y: dC + brLen }, v6 = { x: xBranchStart, y: dC }, v7 = { x: 0, y: dA };
  const contornoRecto = [v0, v1, v2, v3, v4, v5, v6, v7];

  // 10cm como en el codo, recortado si la pieza no da de sí (rama corta o
  // muy estrecha) para que el chaflán nunca se autointerseque con las
  // aristas adyacentes (chamfer siempre menor que la mitad de cada una).
  const chamfer = Math.max(10, Math.min(100, Math.floor(brLen * 0.6), Math.floor(dB * 0.5), Math.floor(dC * 0.5), Math.floor(dA * 0.5)));

  // v3 está en el índice 3; v6, tras insertar los 2 puntos del primer
  // chaflán en su lugar, pasa del índice 6 al 7 en el contorno resultante.
  const corte1 = achaflanarVertice(contornoRecto, 3, chamfer);
  const corte2 = achaflanarVertice(corte1.contorno, 7, chamfer);
  const contorno = corte2.contorno;

  const lineas = [
    linea(0, 0, Lspine, 0, { herramienta: 'Referencia', etiqueta: `Columna a lo largo del conducto principal, ${mmACm(Lspine)}cm de largo` }),
    linea(0, 0, 0, dA, { herramienta: 'Cuchillo', etiqueta: `Boca entrada = ANCHO_A+2 = ${mmACm(dA)}cm` }),
    linea(Lspine, 0, Lspine, dC, { herramienta: 'Cuchillo', etiqueta: `Boca salida recta = ANCHO_C+2 = ${mmACm(dC)}cm` }),
    linea(xBranchStart, dC + brLen, xBranchEnd, dC + brLen, { herramienta: 'Cuchillo', etiqueta: `Boca rama = ANCHO_B+2 = ${mmACm(dB)}cm` }),
    linea(v3.x, v3.y, corte1.m1.x, corte1.m1.y, { herramienta: 'Referencia', etiqueta: `Marca a ${mmACm(chamfer)}cm desde la esquina rama/salida` }),
    linea(v3.x, v3.y, corte1.m2.x, corte1.m2.y, { herramienta: 'Referencia', etiqueta: `Marca a ${mmACm(chamfer)}cm desde la esquina rama/salida` }),
    linea(corte1.m1.x, corte1.m1.y, corte1.m2.x, corte1.m2.y, { herramienta: 'Cuchillo', etiqueta: 'Chaflán a 45º entre las dos marcas' }),
    linea(v6.x, v6.y, corte2.m1.x, corte2.m1.y, { herramienta: 'Referencia', etiqueta: `Marca a ${mmACm(chamfer)}cm desde la esquina rama/entrada` }),
    linea(v6.x, v6.y, corte2.m2.x, corte2.m2.y, { herramienta: 'Referencia', etiqueta: `Marca a ${mmACm(chamfer)}cm desde la esquina rama/entrada` }),
    linea(corte2.m1.x, corte2.m1.y, corte2.m2.x, corte2.m2.y, { herramienta: 'Cuchillo', etiqueta: 'Chaflán a 45º entre las dos marcas' }),
  ];

  // 3 paredes reales, caminando el contorno YA achaflanado (10 vértices:
  // 0=v0, 1=v1, 2=v2, 3=corte1.m1, 4=corte1.m2, 5=v4, 6=v5, 7=corte2.m1,
  // 8=corte2.m2, 9=v7) entre bocas consecutivas — salida recta(1-2) →
  // rama(5-6) → entrada(9-0) → salida(1-2). La pared recta inferior (0→1)
  // no tiene ningún vértice intermedio, así que no necesita corrección de
  // esquina. Las otras 2 sí caminan un vértice intermedio cada una (el
  // chaflán), y ahí se aplica automáticamente la regla del cap. 4.1.
  const alto = altoConductoMm || anchoAMm;
  const paredRecta = piezaPared('Pared recta inferior', [longitudSegmento(v0, v1)], alto);
  const paredDerecha = piezaParedDesdeContorno('Pared lateral derecha (salida a rama)', contorno, 2, 5, alto);
  const paredIzquierda = piezaParedDesdeContorno('Pared lateral izquierda (rama a entrada)', contorno, 6, 9, alto);

  return {
    tipo: 'derivacion',
    titulo: `Pantalón/Y — entrada ${Math.round(A)} / rama ${Math.round(B)} / salida ${Math.round(C)} mm`,
    tapa: { anchoMm: Lspine, altoMm: dC + brLen, lineas, contornoMm: contorno, unidades: 2 },
    notas: [
      'Reconstrucción propia con chaflán a 45º en la unión rama/columna, contrastada contra una foto real de instalación — no la página literal del manual (no disponible en imagen). Revisa esta pieza antes de cortar.',
      'Según el manual (cap. 6.1), la pared recta (entrada↔salida) se hace en la práctica como 2 piezas: una fija y una de cierre, medida y cortada tras montar el resto, con 5cm de solape para sellar contra la pared lateral — aquí se calcula como una sola pieza para la lista de materiales.',
      'Boca de entrada: hembra. Bocas de salida (recta y rama): macho con la herramienta negra tras cortar.',
      'Se necesitan 2 tapas iguales (superior e inferior).',
    ],
    paredes: [paredRecta, paredIzquierda, paredDerecha],
  };
}

// Reducción / embocadura a dos lados (cap. 8.2): NO es un trapecio puro —
// tiene un tramo recto de 10cm a la anchura de cada boca (para poder
// mecanizar hembra/macho sin que la propia inclinación lo estorbe) y el
// estrechamiento solo ocurre en el tramo central de 40cm, centrado en el
// eje de la boca ancha (mitad de ANCHO_A). Total 10+40+10 = 60cm, tal como
// da el ejemplo del propio manual (equipo 100cm → ducto 50cm).
export function fabricacionReduccion(anchoEquipoMm, altoEquipoMm, anchoDuctoMm, altoDuctoMm) {
  const A = anchoEquipoMm, B = anchoDuctoMm;
  const dA = A + 20, dB = B + 20;
  const eje = dA / 2;
  const v0 = { x: 0, y: 0 }, v1 = { x: dA, y: 0 }, v2 = { x: dA, y: 100 };
  const v3 = { x: eje + dB / 2, y: 500 }, v4 = { x: eje + dB / 2, y: 600 };
  const v5 = { x: eje - dB / 2, y: 600 }, v6 = { x: eje - dB / 2, y: 500 }, v7 = { x: 0, y: 100 };
  const contorno = [v0, v1, v2, v3, v4, v5, v6, v7];
  const lineas = [
    linea(v0.x, v0.y, v1.x, v1.y, { herramienta: 'Cuchillo', etiqueta: `Boca ancha (equipo) = ANCHO_A+2 = ${mmACm(dA)}cm — hembra` }),
    linea(v1.x, v1.y, v2.x, v2.y, { herramienta: 'Referencia', etiqueta: '10cm de tramo recto antes del estrechamiento' }),
    linea(v2.x, v2.y, v3.x, v3.y, { herramienta: 'Cuchillo', etiqueta: 'Estrechamiento — 40cm' }),
    linea(v3.x, v3.y, v4.x, v4.y, { herramienta: 'Referencia', etiqueta: '10cm de tramo recto tras el estrechamiento' }),
    linea(v4.x, v4.y, v5.x, v5.y, { herramienta: 'Cuchillo', etiqueta: `Boca estrecha (ducto) = ANCHO_B+2 = ${mmACm(dB)}cm — macho` }),
    linea(v5.x, v5.y, v6.x, v6.y, { herramienta: 'Referencia', etiqueta: '10cm de tramo recto tras el estrechamiento' }),
    linea(v6.x, v6.y, v7.x, v7.y, { herramienta: 'Cuchillo', etiqueta: 'Estrechamiento — 40cm' }),
    linea(v7.x, v7.y, v0.x, v0.y, { herramienta: 'Referencia', etiqueta: '10cm de tramo recto antes del estrechamiento' }),
  ];
  const altoConducto = altoDuctoMm || altoEquipoMm;
  return {
    tipo: 'reduccion',
    titulo: `Embocadura / reducción — ${Math.round(A)} a ${Math.round(B)} mm`,
    tapa: { anchoMm: dA, altoMm: 600, contornoMm: contorno, lineas, ladosMm: ladosPoligono(contorno), unidades: 2 },
    notas: [
      `Sección de salida del equipo estimada en ${Math.round(A)}×${Math.round(altoEquipoMm)}mm a partir de su caudal total — ajusta esta pieza si la ficha técnica del equipo da una boca distinta.`,
      'Si el alto también cambia respecto al ducto, se resuelve con la altura de las paredes laterales, no con la tapa.',
      'Boca ancha (equipo): mecanizado hembra. Boca estrecha (ducto): mecanizado macho.',
    ],
    paredes: [
      piezaParedDesdeContorno('Pared lateral derecha', contorno, 1, 4, altoConducto),
      piezaParedDesdeContorno('Pared lateral izquierda', [v5, v6, v7, v0], 0, 3, altoConducto),
    ],
  };
}

// Descompone una ficha de fabricación (tapa ×N + paredes) en piezas de
// encaje/plancha REALES — antes cada figura entera se aproximaba como un
// único cuadrado (√área) para el nesting, lo que además de subestimar el
// material hacía imposible dibujar la forma real en el plano de corte. Cada
// tapa individual y cada pared salen ahora como su propia pieza, con sus
// dimensiones reales y un `dibujo` mínimo (contorno+líneas o tira) para que
// el PDF pueda redibujar la forma exacta dentro de su hueco en la plancha.
// Solo la primera pieza lleva el objeto `fabricacion` completo, para que la
// deduplicación de fichas (por título) y la cuenta de "unidades" de la
// lista de materiales sigan contando 1 figura, no 1 por cada tapa/pared.
function piezasDesdeFabricacion(fabricacion, tramoId) {
  const piezas = [];
  let primera = true;
  const marcar = (p) => { if (primera) { p.fabricacion = fabricacion; primera = false; } return p; };

  if (fabricacion.tapa) {
    const { anchoMm, altoMm, contornoMm, lineas, unidades = 1 } = fabricacion.tapa;
    for (let i = 0; i < unidades; i++) {
      piezas.push(marcar({
        tramoId,
        etiqueta: `Tapa ${i + 1}/${unidades} — ${fabricacion.titulo}`,
        anchoPiezaMm: Math.round(altoMm),
        largoPiezaMm: Math.round(anchoMm),
        areaM2: (anchoMm * altoMm) / 1e6,
        dibujo: { tipo: 'tapa', anchoMm, altoMm, contornoMm, lineas },
      }));
    }
  }
  for (const pared of fabricacion.paredes || []) {
    piezas.push(marcar({
      tramoId,
      etiqueta: `${pared.nombre.split(' —')[0].split(' (')[0]} — ${fabricacion.titulo}`,
      anchoPiezaMm: pared.anchoPiezaMm,
      largoPiezaMm: pared.largoPiezaMm,
      areaM2: (pared.anchoPiezaMm * pared.largoPiezaMm) / 1e6,
      dibujo: { tipo: 'tira', tira: pared.tira },
    }));
  }
  return piezas;
}

// Detecta a qué se conecta el arranque de cada tramo (el equipo, o el
// final de otro tramo) por proximidad — mismo criterio geométrico que ya
// usa PlanoTracer.js para dibujar la embocadura equipo→tramo, aquí
// reutilizado para saber quién alimenta a quién y poder sumar caudales.
// El punto de enganche se busca en TODO el trazado del otro tramo (no solo
// en su último punto) con puntoMasCercanoEnPolilinea — así una derivación
// puede arrancar en mitad de un tronco largo, no solo en sus extremos, que
// es como se diseña una instalación real (un tronco del equipo al punto más
// lejano, con derivaciones colgando de él en el camino).
// Solo se consideran tramos con trazadoPx (los añadidos a mano, sin
// geometría, quedan siempre sin padre — se comportan como antes).
export function detectarConexiones(tramos, equipoPos, tolPx = TOL_CONEXION_PX) {
  const padrePorTramoId = {};
  const puntoAtaquePorTramoId = {};
  const conTrazado = tramos.filter(t => t.trazadoPx?.length >= 2);

  for (const t of conTrazado) {
    const inicio = t.trazadoPx[0];
    let mejor = null, mejorDist = tolPx, mejorPunto = null;
    if (equipoPos) {
      const d = distanciaPx(inicio, equipoPos);
      if (d < mejorDist) { mejor = { tipo: 'equipo' }; mejorDist = d; mejorPunto = equipoPos; }
    }
    for (const otro of conTrazado) {
      if (otro.id === t.id) continue;
      const cercano = puntoMasCercanoEnPolilinea(inicio, otro.trazadoPx);
      if (cercano && cercano.distancia < mejorDist) {
        mejor = { tipo: 'tramo', id: otro.id };
        mejorDist = cercano.distancia;
        mejorPunto = cercano.punto;
      }
    }
    padrePorTramoId[t.id] = mejor;
    if (mejor) puntoAtaquePorTramoId[t.id] = mejorPunto;
  }

  // Corta cadenas cicladas (trazado erróneo que se realimenta a sí mismo):
  // si seguir los padres desde un tramo vuelve a pasar por él, se le quita
  // el padre y se avisa, en vez de dejar que la propagación recursiva entre
  // en bucle infinito.
  const avisos = [];
  for (const t of conTrazado) {
    const visitados = new Set([t.id]);
    let actual = padrePorTramoId[t.id];
    while (actual?.tipo === 'tramo') {
      if (visitados.has(actual.id)) {
        avisos.push(`Se detectó un ciclo de conexión en el trazado que involucra a "${t.nombre || t.id}" — revisa cómo arrancan y terminan esos tramos.`);
        padrePorTramoId[t.id] = null;
        break;
      }
      visitados.add(actual.id);
      actual = padrePorTramoId[actual.id];
    }
  }

  return { padrePorTramoId, puntoAtaquePorTramoId, avisos };
}

// Agrupa los tramos por punto de unión FÍSICO real (padre + proximidad entre
// sus puntos de ataque, no solo por padre) — así dos derivaciones que
// cuelgan del mismo tronco pero en puntos distintos no se confunden en una
// sola pieza, y cada una lleva su propia T. `esFinalDePadre` marca si el
// enganche cae en el último vértice del tramo padre (caso "pantalón"/Y, el
// tronco termina ahí) o en mitad de su trazado (caso "T", el tronco sigue).
function agruparJunturas(tramos, tramosPorId, padrePorTramoId, puntoAtaquePorTramoId, tolPx) {
  const grupos = [];
  for (const t of tramos) {
    const padre = padrePorTramoId[t.id];
    if (!padre) continue;
    const punto = puntoAtaquePorTramoId[t.id];
    let grupo = grupos.find(g => g.padre.tipo === padre.tipo && g.padre.id === padre.id && distanciaPx(g.punto, punto) < tolPx);
    if (!grupo) {
      const esFinalDePadre = padre.tipo === 'equipo'
        ? true
        : distanciaPx(punto, tramosPorId[padre.id].trazadoPx[tramosPorId[padre.id].trazadoPx.length - 1]) < 5;
      grupo = { padre, punto, hijos: [], esFinalDePadre };
      grupos.push(grupo);
    }
    grupo.hijos.push(t.id);
  }
  return grupos;
}

// Caudal total de cada tramo = el suyo propio (estancias que alimenta
// directamente) + el de todos los tramos que cuelgan de él, recursivo. Con
// 0 o 1 hijos coincide exactamente con caudalDirectoPorTramoId — un
// proyecto sin ramificaciones no cambia de resultado.
function propagarCaudal(tramos, caudalDirectoPorTramoId, padrePorTramoId) {
  const hijosPorPadre = {}; // 'equipo' | tramoId -> [tramoId]
  for (const t of tramos) {
    const padre = padrePorTramoId[t.id];
    if (!padre) continue;
    const clave = padre.tipo === 'equipo' ? 'equipo' : padre.id;
    (hijosPorPadre[clave] ||= []).push(t.id);
  }

  const caudalTotalPorTramoId = {};
  const enCalculo = new Set();
  const calcular = (id) => {
    if (caudalTotalPorTramoId[id] !== undefined) return caudalTotalPorTramoId[id];
    if (enCalculo.has(id)) return caudalDirectoPorTramoId[id] || 0; // guarda extra ante ciclos no filtrados
    enCalculo.add(id);
    const hijos = hijosPorPadre[id] || [];
    const total = (caudalDirectoPorTramoId[id] || 0) + hijos.reduce((sum, hijoId) => sum + calcular(hijoId), 0);
    enCalculo.delete(id);
    caudalTotalPorTramoId[id] = total;
    return total;
  };
  for (const t of tramos) calcular(t.id);

  return { caudalTotalPorTramoId, hijosPorPadre };
}

// Pieza de unión en cada punto de enganche real (uno por grupo de
// agruparJunturas, no uno por padre): un enganche en el EXTREMO final de un
// padre necesita ≥2 ramas para ser una unión real (si no, es solo una
// continuación simple, sin pieza) — ese caso es el "pantalón"/Y. Un enganche
// en MITAD del trazado de un padre siempre es una unión real aunque sea una
// sola rama, porque el tronco sigue recto a ambos lados del punto — ese es
// el caso "T", y es justo lo que antes no se detectaba (detectarConexiones
// solo miraba el final de cada tramo).
function piezasUnion(tramos, tramosPorId, grupos, areaTeAproxM2, seccionPorTramoId) {
  const piezas = [];
  const juntas = [];
  for (const grupo of grupos) {
    const { padre, punto, hijos, esFinalDePadre } = grupo;
    if (esFinalDePadre && hijos.length < 2) continue; // continuación simple, no es una unión real
    const esEquipo = padre.tipo === 'equipo';
    const nombrePadre = esEquipo ? 'Salida equipo' : (tramosPorId[padre.id]?.nombre || `Tramo ${padre.id}`);

    // Ficha real de derivación (tapa + paredes, ya con dimensiones exactas)
    // cuando se conoce la sección del padre. En el caso T (mitad de
    // trazado) la "salida recta" C reutiliza la sección del propio padre
    // (el tronco sigue igual a ambos lados) — ya es lo que hace el
    // `|| seccionPadre` de abajo cuando solo hay una rama. En el caso Y con
    // más de 2 ramas del mismo punto (pantalón de varias vías, poco
    // habitual) se usa la 2ª rama como "salida C" — aproximación
    // documentada en la propia ficha.
    const seccionPadre = esEquipo ? null : seccionPorTramoId[padre.id];
    if (seccionPadre && seccionPorTramoId[hijos[0]]) {
      const seccionB = seccionPorTramoId[hijos[0]];
      const seccionC = seccionPorTramoId[hijos[1]] || seccionPadre;
      const fabricacion = fabricacionDerivacion(seccionPadre.anchoMm, seccionB.anchoMm, seccionC.anchoMm, seccionPadre.altoMm);
      piezas.push(...piezasDesdeFabricacion(fabricacion, esEquipo ? null : padre.id));
    } else {
      // No se conoce la sección del padre (caso raro: unión directa en el
      // equipo) — no hay con qué calcular una forma real, se deja una
      // pieza aproximada como red de seguridad para que al menos cuente en
      // el total de material.
      const ladoMm = Math.round(Math.sqrt(areaTeAproxM2) * 1000);
      piezas.push({
        tramoId: esEquipo ? null : padre.id,
        etiqueta: `${esFinalDePadre ? 'T/Y' : 'T'} — ${nombrePadre} (${hijos.length} rama${hijos.length === 1 ? '' : 's'})`,
        anchoPiezaMm: ladoMm,
        largoPiezaMm: ladoMm,
        areaM2: areaTeAproxM2,
      });
    }
    juntas.push({ punto, ramas: hijos.length });
  }
  return { piezas, juntas };
}

// Un tramo mide a menudo más de 3m — la plancha no da para una pieza tan
// larga en una sola tirada, así que se parte en varias antes de empaquetar.
// Reparto equitativo (no "a tope") para no dejar un recorte final ridículo:
// una pieza de 3050mm da dos partes de ~1525mm, no 3000mm + un recorte de 50mm.
function dividirPiezasLargas(piezas, panelAnchoMm) {
  const resultado = [];
  for (const pieza of piezas) {
    if (pieza.largoPiezaMm <= panelAnchoMm) { resultado.push(pieza); continue; }
    const partes = Math.ceil(pieza.largoPiezaMm / panelAnchoMm);
    const largoPorParte = pieza.largoPiezaMm / partes;
    for (let i = 0; i < partes; i++) {
      resultado.push({
        ...pieza,
        etiqueta: `${pieza.etiqueta} (parte ${i + 1}/${partes})`,
        largoPiezaMm: largoPorParte,
        areaM2: (pieza.anchoPiezaMm * largoPorParte) / 1e6,
      });
    }
  }
  return resultado;
}

// Empaquetado "shelf first-fit decreasing height": se ordena por el ancho
// de pieza (el perímetro, que fija el grosor de cada fila) de mayor a
// menor, y cada pieza se prueba primero en cualquier fila ya abierta —de
// cualquier plancha— que tenga hueco, antes de abrir fila o plancha nueva.
// Así se rellenan huecos en vez de desperdiciar el resto de cada fila
// (probado: el shelf simple sin first-fit necesitaba 6 planchas para un
// caso que con esto baja a 3).
export function empaquetarPiezas(piezas, panelAnchoMm, panelAltoMm) {
  const avisos = [];
  const validas = [];
  const excluidas = [];
  for (const p of piezas) {
    if (p.anchoPiezaMm > panelAltoMm) {
      avisos.push(`"${p.etiqueta}" mide ${p.anchoPiezaMm}mm de ancho, más que el ancho de la plancha (${panelAltoMm}mm) — no encaja en ninguna orientación (ni siquiera como pieza en L para un tramo recto), revisar manualmente. No cuenta en las planchas ni en el área de abajo (ver "material adicional").`);
      excluidas.push(p);
      continue;
    }
    validas.push(p);
  }

  const divididas = dividirPiezasLargas(validas, panelAnchoMm);
  const ordenadas = [...divididas].sort((a, b) => b.anchoPiezaMm - a.anchoPiezaMm);

  const planchas = [];
  const filas = []; // todas las filas abiertas, de todas las planchas, en orden de creación

  for (const pieza of ordenadas) {
    let fila = filas.find(f => f.xOcupadoMm + pieza.largoPiezaMm <= panelAnchoMm && pieza.anchoPiezaMm <= f.alturaMm);
    if (!fila) {
      let plancha = planchas[planchas.length - 1];
      if (!plancha || plancha.yOcupadoMm + pieza.anchoPiezaMm > panelAltoMm) {
        plancha = { piezas: [], yOcupadoMm: 0 };
        planchas.push(plancha);
      }
      fila = { plancha, yMm: plancha.yOcupadoMm, alturaMm: pieza.anchoPiezaMm, xOcupadoMm: 0 };
      plancha.yOcupadoMm += pieza.anchoPiezaMm;
      filas.push(fila);
    }
    fila.plancha.piezas.push({ ...pieza, x: fila.xOcupadoMm, y: fila.yMm });
    fila.xOcupadoMm += pieza.largoPiezaMm;
  }

  return { planchas: planchas.map(pl => ({ piezas: pl.piezas })), avisos, excluidas };
}

// Decide entre pieza única (cap. 3.1) o 2 piezas en L (cap. 3.2) según si el
// perímetro de la sección cabe en el ancho de la plancha, y genera tanto las
// piezas de plancha reales a empaquetar (1 o 2) como un resumen para la
// tabla de tramos. Si ni siquiera 2 piezas en L caben (secciones enormes,
// fuera de lo que cubre el manual), se devuelven igual — empaquetarPiezas ya
// sabe excluirlas y avisar, igual que hacía antes con la pieza única.
function piezasTramoRecto(tramo, seccion, margenEmpalmeMm, margenPerimetroMm, panelAltoMm) {
  const etiqueta = tramo.nombre || `Tramo ${tramo.id}`;
  const largoPiezaMm = Number(tramo.longitud || 0) * 1000 + margenEmpalmeMm;
  const perimetroMm = 2 * (seccion.anchoMm + seccion.altoMm);
  const anchoUnicaMm = perimetroMm + margenPerimetroMm;

  if (anchoUnicaMm <= panelAltoMm) {
    const fabricacion = fabricacionTramoRecto(seccion.anchoMm, seccion.altoMm, largoPiezaMm);
    const piezaPlancha = {
      tramoId: tramo.id, etiqueta, anchoPiezaMm: anchoUnicaMm, largoPiezaMm,
      areaM2: (anchoUnicaMm * largoPiezaMm) / 1e6, fabricacion,
      dibujo: { tipo: 'tira', tira: fabricacion.tira },
    };
    return {
      resumen: { anchoPiezaMm: anchoUnicaMm, largoPiezaMm, areaM2: piezaPlancha.areaM2, metodo: 'unica', unidades: 1 },
      piezasPlancha: [piezaPlancha],
    };
  }

  const anchoLMm = seccion.anchoMm + seccion.altoMm + 20;
  const fabricacionL = fabricacionTramoRectoDosPiezas(seccion.anchoMm, seccion.altoMm, largoPiezaMm);
  const dibujo = { tipo: 'tira', tira: fabricacionL.tira };
  const piezasPlancha = [1, 2].map(n => ({
    tramoId: tramo.id, etiqueta: `${etiqueta} (pieza L ${n}/2)`, anchoPiezaMm: anchoLMm, largoPiezaMm,
    areaM2: (anchoLMm * largoPiezaMm) / 1e6, fabricacion: fabricacionL, dibujo,
  }));
  return {
    resumen: { anchoPiezaMm: anchoLMm, largoPiezaMm, areaM2: piezasPlancha[0].areaM2 * 2, metodo: 'dosPiezasL', unidades: 2 },
    piezasPlancha,
  };
}

export function calcularProyecto({ estancias = [], tramos = [], config = {}, equipoPos = null, caudalEquipoM3h = 0 }) {
  const cfg = { ...DEFAULTS, ...config };

  const caudalPorEstanciaId = {};
  for (const e of estancias) caudalPorEstanciaId[e.id] = calcularCaudalEstancia(e, cfg.caudalPorM2);

  // Topología de la red detectada por proximidad geométrica (qué tramo
  // arranca pegado a cuál, o al equipo) — de ahí sale tanto la propagación
  // de caudal (un tronco lleva la suma de sus ramas) como las piezas de
  // unión T/Y en cada punto de ramificación.
  const tramosPorId = Object.fromEntries(tramos.map(t => [t.id, t]));
  const { padrePorTramoId, puntoAtaquePorTramoId, avisos: avisosConexion } = detectarConexiones(tramos, equipoPos);
  const caudalDirectoPorTramoId = {};
  for (const t of tramos) caudalDirectoPorTramoId[t.id] = calcularCaudalTramo(t, caudalPorEstanciaId);
  const { caudalTotalPorTramoId } = propagarCaudal(tramos, caudalDirectoPorTramoId, padrePorTramoId);

  const alturaMm = Number(cfg.alturaDisponibleMm) || 0;
  const piezas = [];
  const seccionPorTramoId = {};

  // Dimensionado por recuperación estática (cap. 11.3, ver funciones más
  // arriba): cada tramo necesita la velocidad ya resuelta de su padre, así
  // que hay que recorrer la red en orden topológico (padre antes que hijo)
  // en vez del orden en que el usuario fue añadiendo tramos a la tabla.
  const hijosPorPadreId = {};
  for (const t of tramos) {
    const padre = padrePorTramoId[t.id];
    const clave = !padre || padre.tipo === 'equipo' ? '__raiz__' : padre.id;
    (hijosPorPadreId[clave] = hijosPorPadreId[clave] || []).push(t);
  }
  const velocidadPorTramoId = {};
  const dimensionarRama = (tramo, velocidadAnteriorMs) => {
    const caudalM3h = caudalTotalPorTramoId[tramo.id] || 0;
    const anchoCrudoMm = velocidadAnteriorMs == null
      ? anchoParaVelocidadObjetivo(caudalM3h, alturaMm, cfg.velocidadTroncal)
      : anchoParaRecuperacionEstatica(caudalM3h, alturaMm, Number(tramo.longitud) || 0, velocidadAnteriorMs, cfg.rugosidadDucto, cfg.factorPerdidaLocal, cfg.factorRecuperacion);
    const anchoMm = Math.max(cfg.redondeoSeccionMm, Math.ceil(anchoCrudoMm / cfg.redondeoSeccionMm) * cfg.redondeoSeccionMm);
    const seccion = { anchoMm, altoMm: alturaMm, seccionM2: (anchoMm * alturaMm) / 1e6 };
    const velocidadMs = velocidadEquivalenteMs(caudalM3h, diametroEquivalenteMm(anchoMm, alturaMm));
    seccionPorTramoId[tramo.id] = seccion;
    velocidadPorTramoId[tramo.id] = velocidadMs;
    for (const hijo of hijosPorPadreId[tramo.id] || []) dimensionarRama(hijo, velocidadMs);
  };
  for (const raiz of hijosPorPadreId.__raiz__ || []) dimensionarRama(raiz, null);

  const tramosCalculados = tramos.map(tramo => {
    const caudalM3h = caudalTotalPorTramoId[tramo.id] || 0;
    const seccion = seccionPorTramoId[tramo.id];
    const velocidadMs = velocidadPorTramoId[tramo.id] || 0;
    const { resumen: pieza, piezasPlancha } = piezasTramoRecto(tramo, seccion, cfg.margenEmpalmeMm, cfg.margenPerimetroMm, cfg.panelAltoMm);
    piezas.push(...piezasPlancha);

    const codos = Number(tramo.codos) || 0;
    for (let i = 0; i < codos; i++) {
      piezas.push(...piezasDesdeFabricacion(fabricacionCodo90(seccion.anchoMm, seccion.altoMm), tramo.id));
    }

    const padre = padrePorTramoId[tramo.id];
    const conectadoA = !padre ? null
      : padre.tipo === 'equipo' ? { tipo: 'equipo' }
      : { tipo: 'tramo', id: padre.id, nombre: tramosPorId[padre.id]?.nombre || `Tramo ${padre.id}` };

    return { ...tramo, caudalM3h, seccion, velocidadMs, pieza, conectadoA };
  });

  // Piezas de unión — un grupo por punto de enganche físico real (no por
  // padre), para que dos derivaciones colgando del mismo tronco en puntos
  // distintos generen cada una su propia T. Ya se conoce la sección real de
  // cada tramo, así que la ficha de fabricación usa los anchos reales.
  const gruposJuntura = agruparJunturas(tramos, tramosPorId, padrePorTramoId, puntoAtaquePorTramoId, TOL_CONEXION_PX);
  const { piezas: piezasDeUnion, juntas } = piezasUnion(tramos, tramosPorId, gruposJuntura, cfg.areaTeAproxM2, seccionPorTramoId);
  piezas.push(...piezasDeUnion);

  // Embocadura: donde un tramo arranca en el equipo, se genera la reducción
  // real que lo conecta — antes solo era un dibujo de referencia en el
  // plano (PlanoTracer.js), sin plancha calculada. La app no pide la boca
  // de salida real del equipo, así que se estima a partir de su caudal
  // total a una velocidad de salida típica (5 m/s); se avisa de ello en la
  // propia ficha para que se ajuste si la ficha técnica del equipo da otra.
  const embocaduras = [];
  const raicesDelEquipo = tramos.filter(t => padrePorTramoId[t.id]?.tipo === 'equipo');
  if (raicesDelEquipo.length && caudalEquipoM3h > 0) {
    const seccionEquipo = calcularSeccion({ caudalM3h: caudalEquipoM3h, velocidad: 5, alturaMm, redondeoMm: cfg.redondeoSeccionMm });
    for (const t of raicesDelEquipo) {
      const seccionDucto = seccionPorTramoId[t.id];
      const fabricacion = fabricacionReduccion(seccionEquipo.anchoMm, seccionEquipo.altoMm, seccionDucto.anchoMm, seccionDucto.altoMm);
      piezas.push(...piezasDesdeFabricacion(fabricacion, t.id));
      embocaduras.push({ tramoId: t.id, fabricacion });
    }
  }

  const { planchas, avisos, excluidas } = empaquetarPiezas(piezas, cfg.panelAnchoMm, cfg.panelAltoMm);

  // areaBrutaM2/areaTotalM2 solo cuentan piezas que de verdad están dentro de
  // alguna de las "planchas" devueltas — si contáramos también las excluidas
  // (perímetro más ancho que la plancha, ver aviso de arriba) el área pedida
  // no cuadraría con numPlanchas: se pediría fibra de más sin que ese extra
  // esté representado en ninguna plancha del despiece. Esas piezas excluidas
  // quedan aparte, en areaAdicionalM2, para que se pueda comprar ese material
  // extra a sabiendas (no se puede calcular su encaje automáticamente: hace
  // falta decidir a mano cómo unir tiras para una sección tan ancha).
  const excluidasSet = new Set(excluidas);
  const piezasEmpaquetadas = piezas.filter(p => !excluidasSet.has(p));
  const areaBrutaM2 = piezasEmpaquetadas.reduce((sum, p) => sum + p.areaM2, 0);
  const areaTotalM2 = areaBrutaM2 * (1 + cfg.merma);
  const areaAdicionalM2 = excluidas.reduce((sum, p) => sum + p.areaM2, 0) * (1 + cfg.merma);

  // Fichas de fabricación deduplicadas por tipo+dimensiones, para no repetir
  // la misma explicación paso a paso varias veces si hay piezas idénticas.
  // La clave se saca del propio título de la ficha (ya incluye las
  // dimensiones reales), no de anchoPiezaMm/largoPiezaMm de la pieza — para
  // los codos y las uniones T/Y esas medidas son un tamaño de plancha
  // aproximado (fijo, igual para todas), no la sección real del conducto, así
  // que dos piezas con anchos distintos podrían compartir esa medida
  // aproximada y acabar mostrando por error las instrucciones de la primera.
  // largoTotalMm acumula la longitud real de cada pieza (con sentido sobre
  // todo para tramoRecto, donde varias piezas comparten ficha por tener la
  // misma sección aunque midan distinto — para la lista de materiales, que
  // necesita "X metros de tramo de tal medida", no solo "X unidades").
  const fichasPorClave = {};
  for (const p of piezas) {
    if (!p.fabricacion) continue;
    const clave = p.fabricacion.titulo;
    if (!fichasPorClave[clave]) fichasPorClave[clave] = { ...p.fabricacion, clave, cantidad: 0, largoTotalMm: 0 };
    fichasPorClave[clave].cantidad++;
    fichasPorClave[clave].largoTotalMm += p.largoPiezaMm;
  }

  return {
    tramosCalculados,
    planchas,
    juntas,
    embocaduras,
    fichasFabricacion: Object.values(fichasPorClave),
    resumen: {
      numPlanchas: planchas.length,
      areaBrutaM2: Math.round(areaBrutaM2 * 100) / 100,
      areaTotalM2: Math.round(areaTotalM2 * 100) / 100,
      areaAdicionalM2: Math.round(areaAdicionalM2 * 100) / 100,
      caudalTotalEstanciasM3h: Object.values(caudalPorEstanciaId).reduce((a, b) => a + b, 0),
      avisos: [...avisosConexion, ...avisos],
    },
  };
}
